import { useEffect, useState } from "react";
import { de, el } from "date-fns/locale";
import { Calendar } from "../components/ui/calendar";
import { api } from "./api";
import { isoDate, monthKey, parseIso, prettyDate } from "./utils";
import { useBT } from "./i18n";
import { WaitlistForm } from "./WaitlistForm";
import { Loader2 } from "lucide-react";

const DFNS = { de, el };

export const DateTimePicker = ({ service, date, setDate, time, setTime, onFullDay }) => {
  const { t, lang, locale } = useBT();
  const [month, setMonth] = useState(date ? parseIso(date) : new Date());
  const [days, setDays] = useState({});
  const [full, setFull] = useState([]);
  const [slots, setSlots] = useState(null);

  useEffect(() => {
    setDays({});
    api.get("/api/public/availability/days", { params: { service: service.id, month: monthKey(month) } })
      .then((r) => { setDays(r.data.days); setFull(r.data.full || []); })
      .catch(() => { setDays({}); setFull([]); });
  }, [service.id, month]);

  useEffect(() => {
    if (!date) return setSlots(null);
    setSlots(null);
    api.get("/api/public/availability", { params: { service: service.id, date } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [service.id, date]);

  useEffect(() => { onFullDay?.(slots?.length === 0); }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  const disabled = (d) => !(days[isoDate(d)] > 0) && !full.includes(isoDate(d));

  return (
    <div className="grid md:grid-cols-[auto_1fr] gap-8" data-testid="book-step-datetime">
      <div className="self-start">
        <div className="bg-white border rounded-xl p-2">
          <Calendar mode="single" weekStartsOn={1} month={month} onMonthChange={setMonth} locale={DFNS[lang]}
            selected={date ? parseIso(date) : undefined} disabled={disabled} fromDate={new Date()}
            onSelect={(d) => { if (d) { setDate(isoDate(d)); setTime(null); } }}
            modifiers={{ full: full.map(parseIso) }}
            modifiersClassNames={{ full: "line-through decoration-[#b09873] text-[#1d2b31]/50" }}
            classNames={{ day_today: "underline underline-offset-4 decoration-[#b09873]" }}
            data-testid="book-calendar" />
        </div>
        {full.length > 0 && <p className="text-[11px] text-[#1d2b31]/60 mt-2 max-w-[280px]" data-testid="book-full-legend">{t.fullLegend}</p>}
      </div>
      <div>
        {!date && <p className="text-sm text-[#1d2b31]/60">{t.pickDate}</p>}
        {date && (
          <>
            <p className="text-xs uppercase tracking-[0.2em] text-[#8a7347]" data-testid="book-selected-date">{prettyDate(date, locale)}</p>
            {slots === null && <Loader2 className="animate-spin mt-6 text-[#b09873]" size={20} />}
            {slots?.length === 0 && (
              <>
                <p className="mt-4 text-sm" data-testid="book-no-slots">{t.fullDay}</p>
                <WaitlistForm key={date} service={service} date={date} />
              </>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
              {slots?.map((s) => (
                <button key={s.time} data-testid={`book-slot-${s.time}`} onClick={() => setTime(s.time)}
                  className={`py-3 rounded-lg text-sm border transition-colors ${
                    time === s.time ? "bg-[#15262d] text-white border-[#15262d]" : "bg-white hover:border-[#b09873]"}`}>
                  {s.time}
                  {s.free_units != null && s.free_units <= 2 && (
                    <span className={`block text-[10px] ${time === s.time ? "text-white/70" : "text-[#8a7347]"}`}>{s.free_units} {t.left}</span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
